import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { useHookstate } from '@hookstate/core';
import { getAllMaterialGroups } from '~/features/Resources/warehouse/materialGroups/services';
import IMaterialGroup from '~/features/Resources/warehouse/materialGroups/interfaces/IMaterialGroup';

interface FilterAutocompleteMaterialGroupProps {
  readonly onMaterialGroupChange: (value: number | null) => void;
  readonly materialGroupId?: number | null;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly isRequired?: boolean;
}

export default function FilterAutocompleteMaterialGroup({
  disabled,
  onMaterialGroupChange,
  materialGroupId,
  error = false,
  isRequired = false,
}: FilterAutocompleteMaterialGroupProps) {
  const { translate } = useLang();
  const [options, setOptions] = useState<readonly IMaterialGroup[]>([]);
  const [value, setValue] = useState<IMaterialGroup | null>(null);
  const loading = useHookstate(false);

  useEffect(() => {
    loading.set(true);
    getAllMaterialGroups().then((materialGroups) => {
      setOptions(materialGroups);
      loading.set(false);
    });
  }, []);

  useEffect(() => {
    if (!materialGroupId) {
      setValue(null);
    } else {
      const option = options.find((option) => option.id === materialGroupId);
      setValue(option ?? null);
    }
  }, [options, materialGroupId]);

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        id="filter-material-group"
        options={options}
        disabled={disabled}
        value={value}
        noOptionsText={translate('No options')}
        size="small"
        loading={loading.get()}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name ?? ''}
        onChange={(_, materialGroup) => {
          setValue(materialGroup);
          onMaterialGroupChange(materialGroup ? Number(materialGroup.id) : null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`${translate('Material group')}${isRequired ? ' *' : ''}`}
            error={error}
          />
        )}
        clearText={translate('Clear')}
      />
    </FormControl>
  );
}
